import React, { useEffect } from "react";
import { Box, Typography } from "@mui/material";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
} from "recharts";
import {
  useFetchImportanceQuery,
  useFetchTimeQuery,
} from "../../../services/roleReviewService";
import useAuth from "../../../hooks/useAuth";
import { styled } from "@mui/system";
import { useLocation } from "react-router-dom";

// Adjust the import path based on your setup
export const COLORS = [
  "#0c8ccc", // Shade of Primary
  "#F69754", // Lighter Orange
  "#1085ab", // Accent Brand Color
  "#F8A36E", // Even Lighter Orange
  "#3ea5c8", // Shade of Accent
  "#F9AF87", // Very Light Orange
  "#6bb5d5", // Shade of Accent
  "#FBC0A1", // Pale Orange
  "#096bb6", // Shade of Primary
  "#F48B3A", // Slightly Lighter Orange
  "#064b73", // Primary Brand Color
  "#F28021", // Secondary Brand Color (Base Orange)
  "#c5dff0", // Shade of Accent
  "#f4a768",
];

// Use a Map to store colors for each unique area
const areaColorMap = new Map();
let currentColorIndex = 0;

export const getColorByArea = (area) => {
  const normalizedArea = area.trim().toLowerCase();
  
  // If a color is already assigned to this area, return it.
  if (areaColorMap.has(normalizedArea)) {
    return areaColorMap.get(normalizedArea);
  }

  // If we haven't run out of colors, assign the next available color.
  if (currentColorIndex < COLORS.length) {
    const assignedColor = COLORS[currentColorIndex];
    areaColorMap.set(normalizedArea, assignedColor);
    currentColorIndex++;
    return assignedColor;
  }

  // If we've used all predefined colors, generate a random one
  // (or you can choose to recycle colors if that is acceptable)
  const randomColor =
    "#" + Math.floor(Math.random() * 16777215).toString(16).padStart(6, "0");
  areaColorMap.set(normalizedArea, randomColor);
  return randomColor;
};
const RADIAN = Math.PI / 180;

const renderCustomizedLabel = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  percent,
}) => {
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill="black"
      fontSize="10"
      textAnchor={x > cx ? "start" : "end"}
      dominantBaseline="central"
    >
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};


const DashboardCharts = ({ userId }) => {
  const { users } = useAuth();
  // const userId=users?.data?.user_id
  const location = useLocation(); 
  const isJwtUser = location.state?.isJwtUser || false;
  const userIdFromState = location.state?.userId;
  let employeeId;
 

  if (isJwtUser) {
    employeeId = userIdFromState;
    console.log("isJwtUser",employeeId) // Use userId from state for JWT-based users
    // JWT users are treated as employees
  } else if (users?.role === "manager" || users?.role === "superadmin") {
    employeeId = userId; // Use the userId prop for managers and superadmins
    // Set role as manager
  } else {
    employeeId = users?.user_id;
    console.log("Normal",employeeId) // Use the logged-in user's ID for non-managers
    // Use the logged-in user's role
  }
  // let employeeId;
  // if (users?.role === "manager") {
  //   employeeId = userId; // Use the userId prop for managers
  // } else {
  //   employeeId = users?.user_id; // Use the logged-in user's ID for non-managers
  // }
  const { data: chartData, error, isLoading,refetch:fetchTimeSpent } = useFetchTimeQuery(employeeId);
  
  const {
    data: ImpData,
    refetch:Imp_data
  } = useFetchImportanceQuery(employeeId);
  useEffect(() => {
        
        
    if (employeeId) {
      fetchTimeSpent()
      Imp_data(); // Refetch data if employeeId is set
    }
  }, [employeeId,fetchTimeSpent,Imp_data]);
  // console.log(ImpData);
  //   console.log(chartData?.data)
  if (isLoading) return <Typography>Loading...</Typography>;
  if (error) return <Typography>Error: {error.message}</Typography>;
 
  

  const timeSpentData = chartData
    ? chartData.data.map((item) => ({
        area: item.area,
        percentage: item.time_spent,
        color: getColorByArea(item.area), // Assuming `time_spent` is available directly
      }))
    : [];
  //   console.log(timeSpentData)
  const importanceData = ImpData
    ? ImpData.data.map((item) => ({
        area: item.area,
        percentage: item.importance,
        color: getColorByArea(item.area),
      }))
    : [];
  //   console.log(importanceData)
  const WrappedYAxisTick = ({ x, y, payload }) => {
    const maxLineLength = 18; // max characters per line (approximate, for wrapping guidance)
    const words = payload.value.toString().split(" ");
 
    const lines = [];
    let currentLine = "";
 
    words.forEach((word) => {
      // Check if adding this word would exceed the max line length
      if ((currentLine + " " + word).trim().length <= maxLineLength) {
        currentLine += (currentLine ? " " : "") + word;
      } else {
        // Push the current line and start a new one
        if (currentLine) lines.push(currentLine);
        currentLine = word;
      }
    });
 
    // Push any remaining line
    if (currentLine) lines.push(currentLine);
 
    return (
      <text x={x} y={y} textAnchor="end" fontSize={12}>
        {lines.map((line, index) => (
          <tspan x={x} dy={index === 0 ? 0 : 14} key={index}>
            {line}
          </tspan>
        ))}
      </text>
    );
  };

  return (
    <>
      <DataChartsBoxWrap>
        <DataChartsBox>
          <DataChartsBoxInner>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row", // Change to row for side-by-side layout
                // justifyContent: "space-between", // Ensure equal spacing between Pie Charts
                rowGap: "20px", // Increased gap between PieCharts
                "@media (max-width: 575px)": { flexWrap: "wrap" },
              }}
            >
              {/* Pie Chart: Time Spent */}
              <Box sx={{ textAlign: "center", fontSize: "0.8rem" }}>
                <Typography sx={{ fontWeight: "bold", fontSize: "15px" }}>
                  Time Spent (%)
                </Typography>
                <PieChart width={230} height={300}>
                  <Pie
                    data={timeSpentData}
                    dataKey="percentage"
                    nameKey="area"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    fill="#8884d8"
                    labelLine={false}
                    label={renderCustomizedLabel}
                  >
                    {timeSpentData.map((item, index) => (
                      <Cell key={`cell-${index}`} fill={item.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  {/* <Legend layout="horizontal" verticalAlign="bottom" align="center" /> */}
                </PieChart>
              </Box>

              {/* Pie Chart: Importance */}
              <Box sx={{ textAlign: "center", fontSize: "0.8rem" }}>
                <Typography sx={{ fontWeight: "bold", fontSize: "15px" }}>
                  Importance (%)
                </Typography>
                <PieChart width={230} height={300}>
                  <Pie
                    data={importanceData}
                    dataKey="percentage"
                    nameKey="area"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    fill="#82ca9d"
                    labelLine={false}
                    label={renderCustomizedLabel}
                  >
                    {importanceData.map((item, index) => (
                      <Cell key={`cell-${index}`} fill={item.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  {/* <Legend layout="horizontal" verticalAlign="bottom" align="center" /> */}
                </PieChart>
              </Box>
              {/* <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  gap: "20px",
                  marginTop: "5px",
                  fontSize: "10px",
                  marginLeft: "50px",
                }}
              >
                {timeSpentData.map((item, index) => (
                  <Box
                    key={`color-box-${index}`}
                    sx={{
                      width: "20px",
                      height: "20px",
                      backgroundColor: `${item.color}`,
                      border: "10px",
                    }}
                  />
                ))}
              </Box> */}
            </Box>
            <Box sx={{ textAlign: "center", marginTop: "10px" }}>
            <PieChart width={460} height={50}>
              <Pie
                data={timeSpentData} // Using timeSpentData since both charts have same categories
                dataKey="percentage"
                nameKey="area"
                cx="50%"
                cy="50%"
                outerRadius={0} // Invisible Pie
                fill="transparent"
              >
                {timeSpentData.map((item, index) => (
                  <Cell key={`cell-${index}`} fill={item.color} />
                ))}
              </Pie>
              <Legend layout="horizontal" verticalAlign="bottom" align="center" />
            </PieChart>
          </Box>
          </DataChartsBoxInner>
          
          <DataChartsBoxInner>
            {/* Bar Chart: Time Spent vs Importance */}
            <Box
              sx={{
                "@media (max-width: 480px)": {
                  "& .recharts-wrapper": {
                    width: "320px !important",
                  },
                  "& .recharts-legend-wrapper": {
                    width: "100% !important",
                  },
                },
              }}
            >
              <Typography variant="h6" gutterBottom>
                Time Spent vs Importance
              </Typography>
              <BarChart
                layout="vertical"
                width={460}
                height={350}
                data={timeSpentData.map((ts) => ({
                  area: ts.area,
                  timeSpent: ts.percentage,
                  importance:
                    importanceData.find((i) => i.area === ts.area)
                      ?.percentage || 0,
                }))}
                margin={{
                  top: 20,
                  right: 30,
                  left: 20,
                  bottom: 5,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                {/* <YAxis type="category" dataKey="area" /> */}
                <YAxis
                  type="category"
                  dataKey="area"
                  tick={<WrappedYAxisTick />}
                  width={100}
                />
                <Tooltip />
                <Legend />
                <Bar dataKey="timeSpent" fill="#F28021" name="Time Spent (%)" />
                <Bar
                  dataKey="importance"
                  fill="#1085AB"
                  name="Importance (%)"
                />
              </BarChart>
            </Box>
          </DataChartsBoxInner>
        </DataChartsBox>
      </DataChartsBoxWrap>
     
    </>
  );
};

export default DashboardCharts;

const DataChartsBoxWrap = styled(Box)(() => ({
  border: "2px solid #6f6f6f",
  background: "#ffffff",
  padding: "15px",
  marginBottom: "15px",
}));
const DataChartsBox = styled(Box)(() => ({
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  marginLeft: "-15px",
  marginRight: "-15px",
}));
const DataChartsBoxInner = styled(Box)(() => ({
  flex: "0 0 auto",
  width: "50%",
  maxWidth: "100%",
  paddingLeft: "15px",
  paddingRight: "15px",
  "@media (max-width: 991px)": {
    width: "100%",
    "& .css-10n1iyp-MuiResponsiveChart-container": {
      margin: "auto",
    },
  },
  "@media (max-width: 575px)": {
    "& .css-10n1iyp-MuiResponsiveChart-container": {
      width: "auto",
      height: "auto",
    },
  },
}));
